import { Component, OnInit, ViewChild, TemplateRef, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, FormArray } from '@angular/forms';
import { ConfigEntityService } from '../../core/services/config-entity.service';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AuthenticationService } from '../../core/services/authentication.service';
import { UserRoleNames } from '../../core/models/users';
import { ActionLogService } from '../../core/services/action-log.service';
import { NavigationService } from 'src/app/core/services/navigation.service';
import { SaveDeploymentSettingsComponent } from '../deployment/modals/save-deployment-settings/save-deployment-settings.component';

@Component({
  selector: 'app-entity-setting',
  templateUrl: './entity-setting.component.html',
  styleUrls: ['./entity-setting.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class EntitySettingComponent implements OnInit {
  settingsForm: FormGroup;
  private initialFormValues: any;
  private dialogRef: any;
  currentUser = this.authenticationService.currentUserValue;
  isReader: boolean = this.currentUser.role === UserRoleNames.Reader;

  @ViewChild('confirmDialog', { static: true }) confirmDialog!: TemplateRef<any>;

  constructor(
    private fb: FormBuilder,
    private configEntityService: ConfigEntityService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private authenticationService: AuthenticationService,
    private actionLogService: ActionLogService,
    private navService: NavigationService,
  ) { }

  ngOnInit(): void {
    this.navService.sendLeftPanelIndex(3);
    this.configEntityService.sendActiveEntity('Entity Setting');
    this.navService.sendChildData('Entity Setting');
    this.navService.sendParentData('');
    this.settingsForm = this.fb.group({
      entities: this.fb.array([]),
    });

    this.loadEntitySettings();
    this.applyDisabledState();
  }

  loadEntitySettings() {
    this.configEntityService.getEntitySettings().subscribe({
      next: (data) => {
        this.initializeForm(data);
        this.initialFormValues = JSON.stringify(this.settingsForm.value); 
      },
      error: (err) => {
        console.error('Error fetching entity settings:', err);
      }
    });
  }

  initializeForm(entities: any[]) {
    const entityFormGroups = entities.map(entity => this.fb.group({
      name: [entity.name],
      setting: this.fb.array(entity.setting.map(setting => this.fb.group({
        name: [setting.name],
        value: [setting.value],
        disabled: [setting.disabled]  
      })))
    }));

    const entityFormArray = this.fb.array(entityFormGroups);
    this.settingsForm.setControl('entities', entityFormArray);
    this.applyDisabledState();
  }

  applyDisabledState() {
    this.entities.controls.forEach(entityGroup => {
      const settingsArray = entityGroup.get('setting') as FormArray;
      settingsArray.controls.forEach(settingGroup => {
        const disabled = settingGroup.get('disabled')?.value || this.isReader; 
        if (disabled) {
          settingGroup.get('value')?.disable(); 
        } else {
          settingGroup.get('value')?.enable(); 
        }
      });
    });
  }

  get entities(): FormArray {
    return this.settingsForm.get('entities') as FormArray;
  }

  onSettingChange(entityIndex: number, settingIndex: number, checked: boolean) {
    if (!this.isReader) { 
      const entityGroup = this.entities.at(entityIndex);
      const settingsArray = entityGroup.get('setting') as FormArray;
      const settingGroup = settingsArray.at(settingIndex);

      settingGroup.get('value')?.setValue(checked);

      const entityName = entityGroup.get('name')?.value;
      const settingName = settingGroup.get('name')?.value;
      const action = checked ? `${settingName} Enabled` : `${settingName} Disabled`;


      
      this.actionLogService.addActionLog(
        settingName,                              
        action,                         
        entityName,
        'EntitySettings',                     
        `${action}  in ${entityName}`  
      );
    }
  }

  openConfirmDialog(): void {
    if (this.isFormUnchanged()) {
      this.snackBar.open('Nothing changed to save.', 'Close', {
        duration: 3000,
        horizontalPosition: 'center',
        verticalPosition: 'bottom' 
      });
      return;
    }

    this.dialogRef = this.dialog.open(SaveDeploymentSettingsComponent, {
      width: '350px',
      data: {
        title: 'Save Changes',
        message: 'Do you want to save applied changes?'
      }
    });

    this.dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.onSubmit();
        this.snackBar.open('Changes are saved.', 'Close', {
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'bottom'
        });
      }
    });
  }

  isFormUnchanged(): boolean {
    return JSON.stringify(this.settingsForm.value) === this.initialFormValues;
  }

  onConfirmNo(): void {
    this.dialogRef.close('no');
  }

  onConfirmYes(): void {
    this.dialogRef.close('yes');
  }

  onSubmit() {
    const entities = JSON.stringify(this.settingsForm.value.entities);

    this.configEntityService.saveEntitySettings(entities).subscribe(
      (response) => {
        },
      (error) => {
        console.error('Error saving settings:', error);
      }
    );
  }
}

